import { useQuery } from "@tanstack/react-query";
import { useConfig } from "wagmi";
import { getPublicClient } from "wagmi/actions";
import { keelVaultFactoryAbi } from "../abi";
import type { RedKeel } from "../config/networks";

/**
 * ¿La factory donde se CREA en esta red admite varios vaults en la misma pool?
 *
 * Se pregunta en vez de darlo por hecho porque las dos generaciones conviven y van a convivir mucho
 * tiempo: las factories anteriores reservan la pool con `vaultFor[dueño][pool]` y revierten con
 * `AlreadyHasVault()`, y las nuevas no. Cablear la respuesta en `networks.ts` habria sido una lista
 * mas que mantener a mano, y de las que no fallan hasta que alguien crea una piscina.
 *
 * El revert ES la respuesta: una factory vieja no tiene `MULTI_VAULT()`, asi que la llamada falla y
 * eso significa exactamente "solo admito uno por pool". Por eso el `catch` devuelve `false` en vez
 * de propagar: no es un error, es la generacion anterior contestando.
 *
 * Ante un fallo de RED —no de contrato— tambien se cae del lado de `false`, que es el prudente: se
 * reutiliza el vault vacio, que funciona en AMBAS. Suponer que si admite varios contra una factory
 * vieja mandaria al usuario a firmar un `createVault` que revierte al final del formulario.
 */
export function useMultiVault(red: RedKeel | undefined) {
  const config = useConfig();

  return useQuery({
    queryKey: ["multiVault", red?.clave, red?.factory],
    enabled: !!red,
    // No cambia nunca para una factory dada: es una constante del bytecode.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    queryFn: async (): Promise<boolean> => {
      const client = getPublicClient(config, { chainId: red!.idCadena });
      if (!client) return false;
      try {
        return await client.readContract({
          address: red!.factory,
          abi: keelVaultFactoryAbi,
          functionName: "MULTI_VAULT",
        });
      } catch {
        return false;
      }
    },
  });
}
